import React, { useEffect, useState, useRef } from 'react';

import './account-user-info.scss';
import { useSelector, useDispatch } from 'react-redux';
import {
  searchUsersWithCriteria,
  searchUsersReset,
  deleteUserReset,
  adminDeleteUserReset,
  downloadUsers,
  downloadAllUsers,
  searchAllUsers,
  searchAllUsersReset,
} from '../../actions/userActions';
import { fetchAssignableRoles, fetchRoles, resetAssignableRoles } from '../../actions/roleActions';
import { Form, Button, ButtonColor, ButtonVariant, ButtonType, Icon, DataTable, FormValidationMode } from '@ampol/design-system';
import { customerUserListFilterSchema, adminUserListFilterSchema } from 'schemas/user-schemas';
import ContentControls from '../../components/content-controls/content-controls';
import FilterControl from '../../components/filter-control/filter-control';
import Status from '../../components/status/status';
import ActionMenu from '../../components/action-menu/action-menu';
import ActionModal from '../../components/_global/action-modal/action-modal';
import UserDeactivatePortalAccessConfirmation from '../../components/user-deactivate-portal-access-confirmation/user-deactivate-portal-access-confirmation';
import Paginator from '../../components/pagination/pagination';
import Env from '../../Env';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';
import { UserState } from 'types/UserState';

const AccUserInfo = ({ onUserSelected }) => {
  const dispatch = useDispatch();

  const isMounted = useRef(false);

  const userStore: UserState = useSelector((state: any) => state.userStore);
  const usersListResult = userStore.users;
  const allUsersListResult = userStore.searchAllUsers;
  const isUsersLoading = userStore.isLoading;
  const isAllUsersLoading = userStore.searchAllUsersLoading;
  const deleteUserLoaded = userStore.deleteUserLoaded;
  const deleteUserLoading = userStore.deleteUserLoading;
  const createUserLoaded = userStore.createUserLoaded;
  const adminDeleteUserLoaded = userStore.adminDeleteUserLoaded;
  const adminDeleteUserLoading = userStore.adminDeleteUserLoading;
  const roleListResult = useSelector((state: any) => state.rolesList.roles);
  const usersListResultSize = userStore.totalSize;
  const allUsersListResultSize = userStore.searchAllUsersResultSize;
  const assignedGrandfatherClientNumber = userStore.assignedGrandfatherClientNumber;
  const canReadWrite = userStore.canReadWriteUsers;
  const updateUserLoaded = userStore.updateUserLoaded;
  const adminUpdateUserLoaded = userStore.adminUpdateUserLoaded;
  const errors = userStore.searchAllUsersError || userStore.isError;

  const loggedInUser = userStore.loggedInUser;
  const assignedAccountNumber = userStore.assignedAccountNumber;

  const defaultFilterValues = {
    firstName: '',
    lastName: '',
    email: '',
    roleId: '',
    accountNumber: '',
  };

  const [filterBarEnabled, setFilterBarEnabled] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [filterValues, setFilterValues] = useState({
    ...defaultFilterValues,
    
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    sortTypeSelected: 'familyName',
    sortDirection: true,
  });
  const [displayDeactivatePortalAccessModal, setDisplayDeactivatePortalAccessModal] = useState(false);
  const [selectedRadioButtonUserId, setSelectedRadioButtonUserId] = useState('');
  const [offsetLeftPosition, setOffsetLeftPosition] = useState(0);
  const [isClearAllClick, setIsClearAllClick] = useState(false);

  const isAdminPortal = Env.isAdminPortal();
  const actionMenuItems = [{
    displayText: 'Deactivate Portal Access', 
    route: '/deactivate-portal-access' 
  }];

  useEffect(() => {
    dispatch(fetchRoles());

    return () => {
      if (isAdminPortal) {
        dispatch(searchAllUsersReset());
        dispatch(adminDeleteUserReset());
      } else {
        dispatch(searchUsersReset());
        dispatch(deleteUserReset());
      }  
    };
  }, []);
  
  useEffect(() => {
    if (isMounted.current) {
      setFilterValues({
        ...defaultFilterValues,
        pageNumber: 1,
        pageSize: 10,
        searchTerm: '',
        sortTypeSelected: 'familyName',
        sortDirection: true,
      });
      setFilterApplied(false);
      setFilterBarEnabled(false);
      setDisplayDeactivatePortalAccessModal(false);
      setSelectedRadioButtonUserId('');
    }

    dispatch(resetAssignableRoles());
    dispatch(fetchAssignableRoles(assignedGrandfatherClientNumber));

    isMounted.current = true;
  }, [assignedGrandfatherClientNumber]);

  useEffect(() => {
    if (deleteUserLoaded || adminDeleteUserLoaded || createUserLoaded || updateUserLoaded || adminUpdateUserLoaded) {
      setFilterValues({
        ...filterValues,
        pageNumber: 1,
      });
    }
  }, [deleteUserLoaded, adminDeleteUserLoaded, createUserLoaded, updateUserLoaded, adminUpdateUserLoaded]);

  useEffect(() => {
    getUsersList();
  }, [filterValues]);

  const getUsersList = () => {
    if (isAdminPortal && !isClearAllClick) {
      dispatch(searchAllUsers(
        filterValues?.searchTerm?.toLowerCase(),
        filterValues?.pageNumber,
        filterValues?.pageSize,
        filterValues?.sortTypeSelected,
        filterValues?.sortDirection,
        filterValues?.firstName,
        filterValues?.lastName,
        filterValues?.email,
        filterValues?.accountNumber,
      ));
    }
    else if (!isClearAllClick) {
      dispatch(searchUsersWithCriteria(
        assignedGrandfatherClientNumber,
        filterValues?.pageNumber,
        filterValues?.pageSize,
        filterValues?.sortTypeSelected,
        filterValues?.sortDirection,
        filterValues?.searchTerm?.toLowerCase(),
        filterValues?.firstName,
        filterValues?.lastName,
        filterValues?.email,
        filterValues?.roleId,
      ));
    }
    setIsClearAllClick(false);
  };

  const handlePageValue = (pageNumber) => {
    setSelectedRadioButtonUserId('');
    setFilterValues({
      ...filterValues,
      pageNumber,
    });

    trackCustomEvent('Change Page Number', loggedInUser, assignedAccountNumber, 'Clicked to change page of users list.');
  };

  const handleSearchUsers = (query) => {
    if (isAdminPortal && query.length > 0 && query.length < 3) {
      return;
    }
    setFilterBarEnabled(false);
    setFilterApplied(false);
    trackCustomEvent('Search Users List', loggedInUser, assignedAccountNumber, 'Searched for user\'s name, email or role.');

    setFilterValues({
      ...filterValues,
      searchTerm: query,
      pageNumber: 1,
    });

    setSelectedRadioButtonUserId('');
  };

  const handleDownload = () => {
    trackCustomEvent('Click Download', loggedInUser, assignedAccountNumber, 'Downloaded the list of users.');

    if (isAdminPortal) {
      dispatch(downloadAllUsers(
        filterValues?.searchTerm?.toLowerCase(),
        filterValues?.firstName,
        filterValues?.lastName,
        filterValues?.email,
      ));
    }
    else {
      dispatch(downloadUsers(
        assignedGrandfatherClientNumber,
        filterValues?.searchTerm?.toLowerCase(),
        filterValues?.firstName,
        filterValues?.lastName,
        filterValues?.email,
        filterValues?.roleId,
      ));
    }
  };

  const handleUsersSort = (column, isAsc) => {
    setFilterValues({
      ...filterValues,
      sortTypeSelected: column,
      sortDirection: isAsc,
      pageNumber: 1,
    });

    setSelectedRadioButtonUserId('');
  };

  const toggleFilter = () => {
    setFilterBarEnabled(!filterBarEnabled);
    setDisplayDeactivatePortalAccessModal(false);
  };

  const handleFilterFormSubmit = (values) => {
    const submittedValues = {
      firstName: values?.firstName,
      lastName: values?.lastName,
      email: values?.email,
      roleId: values?.roleId,
      accountNumber: values?.accountNumber ?? '',
    };
    const isDefaultValues = Object.keys(submittedValues).length === Object.keys(defaultFilterValues).length &&
      Object.keys(submittedValues).every(key => submittedValues[key] === defaultFilterValues[key]);

    setFilterValues({
      ...filterValues,
      ...submittedValues,
      searchTerm: '',
      pageNumber: 1,
    });

    setFilterBarEnabled(!filterBarEnabled);
    setFilterApplied(!isDefaultValues);
    setSelectedRadioButtonUserId('');

    trackCustomEvent('Submit Filter Users', loggedInUser, assignedAccountNumber, 'Clicked Filter button to filter Users List by First Name, Last Name, Email, and/or Roles.');
  };

  const handleClearFilters = () => {
    window.dataLayer.push({
      event: 'Card_Portal_All_Clear_All',
      button_click: true,
    });
    setIsClearAllClick(true);
    setFilterValues({
      ...filterValues,
      ...defaultFilterValues,
    });

  };

  const handleActionMenuClick = (route, offsetLeftPosition) => {
    setOffsetLeftPosition(offsetLeftPosition - 1);

    if (route === '/deactivate-portal-access') {
      setDisplayDeactivatePortalAccessModal(!displayDeactivatePortalAccessModal);
    }

    setFilterBarEnabled(false);
  };

  const closeActionMenuDialog = () => {
    setSelectedRadioButtonUserId('');
    setDisplayDeactivatePortalAccessModal(false);
  };

  const selectUser = (userId) => {
    setSelectedRadioButtonUserId(userId);
  };

  return (
    <>
      <FilterControl
        showFilterControl={true}
        filterToggle={toggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
        showSearch={true}
        handleSearch={handleSearchUsers}
        defaultSearchValue={filterValues?.searchTerm}
      >
        <div className="account-user-info__filter-form">
          <div className="account-user-info__users-filters">
            <Form
              id={'users-filters-form'}
              schema={isAdminPortal ? adminUserListFilterSchema : customerUserListFilterSchema}
              onSubmit={handleFilterFormSubmit}
              defaultValues={filterValues}
              validationMode={FormValidationMode.onChange}
            >
              <div className="account-user-info__users-filters__grid">
                <Form.Input label={'First Name'} name={'firstName'}/>
                <Form.Input label={'Last Name'} name={'lastName'}/>
                <Form.Input label={'Email'} name={'email'}/>
                {isAdminPortal &&
                  <Form.Input label={'Account Number'} name={'accountNumber'}/>
                }
                {!isAdminPortal && <div>
                  <Form.DropDown
                    label="Roles"
                    name="roleId"
                    options={roleListResult.filter(x => x.notActions[0] === 'AdminPortal.Access').map(x => ({ label: x.name, value: x.id }))}
                  />
                </div>}

                <div className="account-user-info__filter-button-container">
                  <Button
                    variant={ButtonVariant.Secondary}
                    color={ButtonColor.Blue}
                    label="Clear All"
                    type={ButtonType.Reset}
                    onClick={handleClearFilters}
                  />
                  <Button
                    variant={ButtonVariant.Primary}
                    color={ButtonColor.Blue}
                    label="Filter"
                    type={ButtonType.Submit}
                    formId={'users-filters-form'}
                  />
                </div>
              </div>
            </Form>
          </div>
        </div>
      </FilterControl>
      <ContentControls
        showPageControls={true}
        showSearch={false}
        selectControl={false}
        showFilterControl={false}
        filterToggle={toggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
        handleDownload={handleDownload}
        totalRecords={isAdminPortal ? allUsersListResult.length : usersListResult.length}
        itemCount={isAdminPortal ? allUsersListResultSize : usersListResultSize}
        pageNumber={filterValues?.pageNumber}
        onPageChange={handlePageValue}
      />

      {selectedRadioButtonUserId &&
        <div>
          <ActionMenu
            items={actionMenuItems}
            clickHandler={handleActionMenuClick}
          />
        </div>
      }

      <div className="account-user-info__deactivate-portal-access">
        {displayDeactivatePortalAccessModal &&
          <ActionModal
            title={[
              'Deactivate Portal Access',
              <Icon
                key='deactivate-portal-access-icon'
                className='account-user-info__deactivate-portal-access-icon'
                url={'/icons/alert.svg'}/>
            ]}
            onClose={closeActionMenuDialog}
            leftPosition={offsetLeftPosition}
          >
            <UserDeactivatePortalAccessConfirmation
              onClose={closeActionMenuDialog}
              selectedUser={selectedRadioButtonUserId}
            />
          </ActionModal>
        }
      </div>

      <div className={'user-table'} >
        <DataTable
          tableVariant={'list'}
          selectType={'single'}
          isSelectable={canReadWrite}
          caption="List of users available to manage"
          columns={
            [
              {
                header: 'First Name',
                accessorKey: 'givenName',
              },
              {
                header: 'Last Name',
                accessorKey: 'familyName',
                defaultSorted: 'asc',
              },
              {
                header: 'Email',
                accessorKey: 'emailId',
                id: 'Email',
              },
              {
                header: 'Status',
                accessorFn: (user) => <Status value={user.status} />,
                id: 'Status',
              },
              {
                id: 'MoreDetails',
                accessorFn: () => <div className={'user-table__more-details'}>View More Details</div>,
              },
            ]
          }
          columnTemplate={'1fr 1fr 1fr 1fr 50px'}
          isLoading={isUsersLoading || isAllUsersLoading || deleteUserLoading || adminDeleteUserLoading}
          noDataMessage={errors ? Validation.CONTENT_ERROR : Validation.NO_CONTENT}
          onColumnSorted={handleUsersSort}
          onRowClick={(user) => onUserSelected(user)}
          onSelect={(user) => selectUser(user.userId)}
          data={isAdminPortal ? allUsersListResult : usersListResult}
          rowSelectedFn={item => selectedRadioButtonUserId && selectedRadioButtonUserId === item?.userId}
          rowSelectionDisabledFn={(cr) => cr?.assignedRoleName === 'Customer Admin'}
          rowIdFn={(row) => row.userId + Math.random()}
        />
      </div>
      <div className="mobile-paginator-control">
        <Paginator
          itemCount={isAdminPortal ? allUsersListResultSize : usersListResultSize}
          pageNumber={filterValues?.pageNumber}
          onPageChange={handlePageValue}
          name={'account-user-info'}
        />
      </div>
    </>
  );
};

export default AccUserInfo;
